import { useEffect, useRef, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { CheckCircle2, XCircle, ArrowRight } from "lucide-react";

const destFor = (bookId) =>
  bookId === "gift" ? "/mon-espace" : bookId === "ceda" ? "/mon-espace/ceda" : bookId === "jeu-ile-de-labondance" ? "/jeu/ile" : bookId ? `/aventure/${bookId}` : "/aventures";

const successText = (bookId) =>
  bookId === "gift" ? "Merci ! Votre cadeau partira à la date choisie, avec votre message. Un reçu vous a été envoyé."
    : bookId === "ceda" ? "Vos capsules CEDA vous attendent. Activez-les quand vous serez prêt(e)."
    : bookId === "jeu-ile-de-labondance" ? "Votre Jeu est débloqué. Les 21 cartes vous attendent." : "Votre aventure peut continuer.";
const successCta = (bookId) =>
  bookId === "gift" ? "VOIR MES CADEAUX" : bookId === "ceda" ? "OUVRIR MES CEDA" : bookId === "jeu-ile-de-labondance" ? "OUVRIR LE JEU" : "CONTINUER L'AVENTURE";

export function PaymentSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("checking"); // checking | paid | error
  const attempts = useRef(0);
  const bookId = sessionStorage.getItem("tao_unlock_book") || "";

  useEffect(() => {
    if (!sessionId) { setStatus("error"); return; }
    let active = true;
    const poll = async () => {
      if (!active) return;
      attempts.current += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") { setStatus("paid"); return; }
        if (["failed", "expired"].includes(data.payment_status)) { setStatus("error"); return; }
      } catch { /* keep polling */ }
      if (attempts.current >= 10) { setStatus("error"); return; }
      setTimeout(poll, 2000);
    };
    poll();
    return () => { active = false; };
  }, [sessionId]);

  return (
    <div className="max-w-lg mx-auto px-4 py-20 md:py-28 text-center" data-testid="payment-success">
      {status === "checking" && (
        <p className="font-sans text-[#1F2A24]/70">Validation de votre paiement…</p>
      )}
      {status === "paid" && (
        <div className="rounded-2xl bg-white border-2 border-[#C9A24B] p-8 md:p-10">
          <CheckCircle2 className="mx-auto text-[#385E3A]" size={48} />
          <h1 className="font-serif text-4xl text-[#1F2A24] mt-4">La porte s'ouvre.</h1>
          <p className="font-serif italic text-lg text-[#1F2A24]/80 mt-3">{successText(bookId)}</p>
          <button
            data-testid="continue-adventure"
            onClick={() => navigate(destFor(bookId))}
            className="mt-7 inline-flex items-center gap-2 rounded-full bg-[#385E3A] text-white px-7 py-3 font-ui text-sm hover:bg-[#2c4a2e] transition-colors"
          >
            {successCta(bookId)} <ArrowRight size={16} />
          </button>
        </div>
      )}
      {status === "error" && (
        <div className="rounded-2xl bg-white border border-[#E8DCC4] p-8">
          <XCircle className="mx-auto text-[#A63B36]" size={40} />
          <h1 className="font-serif text-2xl text-[#1F2A24] mt-3">Paiement non confirmé</h1>
          <p className="font-sans text-sm text-[#1F2A24]/70 mt-2">
            Si vous avez été débité, votre accès sera débloqué sous peu. Réessayez dans un instant.
          </p>
          <button onClick={() => navigate(destFor(bookId))} className="mt-6 rounded-full border border-[#C9A24B] px-6 py-2.5 font-ui text-sm">
            Retour à l'aventure
          </button>
        </div>
      )}
    </div>
  );
}

export function PaymentPaypalReturn() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const orderId = params.get("token");
  const bookId = sessionStorage.getItem("tao_unlock_book") || "";
  const [status, setStatus] = useState("checking");
  const done = useRef(false);

  useEffect(() => {
    if (done.current) return;
    done.current = true;
    if (!orderId) { setStatus("error"); return; }
    api.post("/paypal/capture-order", { order_id: orderId })
      .then(({ data }) => setStatus(data.status === "paid" ? "paid" : "error"))
      .catch(() => setStatus("error"));
  }, [orderId]);

  return (
    <div className="max-w-lg mx-auto px-4 py-20 md:py-28 text-center" data-testid="payment-paypal-return">
      {status === "checking" && <p className="font-sans text-[#1F2A24]/70">Validation de votre paiement PayPal…</p>}
      {status === "paid" && (
        <div className="rounded-2xl bg-white border-2 border-[#C9A24B] p-8 md:p-10">
          <CheckCircle2 className="mx-auto text-[#385E3A]" size={48} />
          <h1 className="font-serif text-4xl text-[#1F2A24] mt-4">La porte s'ouvre.</h1>
          <p className="font-serif italic text-lg text-[#1F2A24]/80 mt-3">{successText(bookId)}</p>
          <button data-testid="continue-adventure" onClick={() => navigate(destFor(bookId))} className="mt-7 inline-flex items-center gap-2 rounded-full bg-[#385E3A] text-white px-7 py-3 font-ui text-sm hover:bg-[#2c4a2e] transition-colors">
            {successCta(bookId)} <ArrowRight size={16} />
          </button>
        </div>
      )}
      {status === "error" && (
        <div className="rounded-2xl bg-white border border-[#E8DCC4] p-8">
          <XCircle className="mx-auto text-[#A63B36]" size={40} />
          <h1 className="font-serif text-2xl text-[#1F2A24] mt-3">Paiement non confirmé</h1>
          <p className="font-sans text-sm text-[#1F2A24]/70 mt-2">Si vous avez été débité, votre accès sera débloqué sous peu. Réessayez dans un instant.</p>
          <button onClick={() => navigate(destFor(bookId))} className="mt-6 rounded-full border border-[#C9A24B] px-6 py-2.5 font-ui text-sm">Retour à l'aventure</button>
        </div>
      )}
    </div>
  );
}

export function PaymentCancel() {
  const navigate = useNavigate();
  const bookId = sessionStorage.getItem("tao_unlock_book") || "";
  return (
    <div className="max-w-lg mx-auto px-4 py-24 text-center" data-testid="payment-cancel">
      <div className="rounded-2xl bg-white border border-[#E8DCC4] p-8">
        <XCircle className="mx-auto text-[#A63B36]/70" size={40} />
        <h1 className="font-serif text-2xl text-[#1F2A24] mt-3">Paiement annulé</h1>
        <p className="font-sans text-sm text-[#1F2A24]/70 mt-2">
          Aucun montant n'a été débité. La porte reste fermée, mais vous pouvez la franchir quand vous le souhaitez.
        </p>
        <button
          onClick={() => { toast("Vous pouvez reprendre l'aventure"); navigate(destFor(bookId)); }}
          className="mt-6 rounded-full bg-[#385E3A] text-white px-6 py-2.5 font-ui text-sm hover:bg-[#2c4a2e]"
        >
          Reprendre l'aventure
        </button>
      </div>
    </div>
  );
}
